import { useQuery, useQueryClient, type QueryKey, type UseQueryOptions } from "@tanstack/react-query";
import { ApiError } from "../api/client";

/**
 * Thin React Query wrapper matching `useAsync`'s return shape (data/loading/
 * error/reload), so pages moving off `useAsync` keep the same rendering code
 * and only change how the fetch is declared. React Query adds caching (a
 * revisited page renders the previous data instantly while refetching in the
 * background) and de-dupes identical in-flight requests across components.
 */
export function useApiQuery<T>(
  queryKey: QueryKey,
  queryFn: () => Promise<T>,
  options?: {
    enabled?: boolean;
    refetchInterval?: UseQueryOptions<T>["refetchInterval"];
  },
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey,
    queryFn,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof ApiError
        ? query.error.message
        : "요청을 처리하지 못했습니다."
      : null,
    reload: () => query.refetch(),
    /** Optimistic local patch — accepts a value or an updater, like `useState`. */
    setData: (value: T | ((prev: T | undefined) => T | undefined)) =>
      queryClient.setQueryData<T>(
        queryKey,
        typeof value === "function" ? (value as (prev: T | undefined) => T | undefined) : value,
      ),
  };
}
